class AoratosGroupCarousel extends HTMLElement {
  async connectedCallback() {
    const lifecycle = this.lifecycle = new AbortController();
    this.featureCleanups = [];
    this.cleanupFunctions = [];
    this.config = JSON.parse(this.dataset.config);
    this.nodes = {
      embla: this.querySelector(':scope > .embla'),
      viewport: this.querySelector(':scope > .embla > .agc-main > .embla__viewport'),
      container: this.querySelector(':scope > .embla > .agc-main > .embla__viewport > .embla__container')
    };
    this.originalItems ??= Array.from(this.nodes.container.children).flatMap(slide => Array.from(slide.children));
    this.mobile = matchMedia('(max-width: 991.98px)');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    try {
      const [engine, helpers, autoplayModule] = await Promise.all([
        import('./embla-extension-rhthUxzc.js'),
        import('./listing-component-shared-xSf0ADsu.js'),
        this.config.autoplay ? import('./embla-carousel-autoplay.esm-lYa57aZD.js') : null
      ]);
      if (lifecycle.signal.aborted) return;
      Object.assign(this, {engine, helpers, autoplayModule});
      const options = {signal: lifecycle.signal};
      this.mobile.addEventListener('change', () => this.init(), options);
      this.reducedMotion.addEventListener('change', () => this.init(), options);
      this.addEventListener('keydown', event => {
        if (event.target !== this.nodes.viewport) return;
        const actions = {ArrowLeft: () => this.emblaApi.scrollPrev(), ArrowRight: () => this.emblaApi.scrollNext(), Home: () => this.emblaApi.scrollTo(0), End: () => this.emblaApi.scrollTo(this.emblaApi.scrollSnapList().length - 1)};
        if (actions[event.key]) { event.preventDefault(); actions[event.key](); }
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const index = this.emblaApi.slideNodes().findIndex(slide => slide.contains(event.target));
        if (index >= 0) this.emblaApi.scrollTo(index, true);
      }, options);
      this.pauseButton = this.querySelector(':scope > .embla > .agc-pause');
      this.pauseButton?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.pauseButton.textContent = this.paused ? 'Play slideshow' : 'Pause slideshow';
        this.syncAutoplay();
      }, options);
      this.addEventListener('pointerenter', event => { this.hovered = event.pointerType !== 'touch'; this.syncAutoplay(); }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.syncAutoplay(); }, options);
      this.addEventListener('focusin', () => this.syncAutoplay(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.syncAutoplay()), options);
      document.addEventListener('visibilitychange', () => this.syncAutoplay(), options);
      this.observer = new IntersectionObserver(([entry]) => { this.nearViewport = entry.isIntersecting; this.syncAutoplay(); }, {rootMargin: '200px'});
      this.observer.observe(this);
      this.init();
    } catch (error) {
      this.disconnectedCallback();
      console.error('Group carousel could not initialize; horizontal scrolling remains available.', error);
    }
  }

  getCurrentGridConfig() { return this.mobile.matches ? this.config.mobile : this.config.desktop; }

  init() {
    const previousItem = this.emblaApi?.slideNodes()[this.emblaApi.selectedScrollSnap()]?.firstElementChild;
    this.helpers.destroyListingEmbla(this);
    const {rows} = this.getCurrentGridConfig();
    if (rows > 1) this.helpers.applyListingGridLayout(this);
    else this.helpers.flattenListingLayout(this);
    const owned = selector => Array.from(this.querySelectorAll(selector)).filter(element => element.closest('aoratos-group-carousel') === this);
    this.nodes.prevBtns = owned('.embla__snip-button--prev button');
    this.nodes.nextBtns = owned('.embla__snip-button--next button');
    for (const button of [...this.nodes.prevBtns, ...this.nodes.nextBtns]) button.type = 'button';
    this.nodes.dots = owned('.embla__snip-dots')[0];
    this.nodes.container.querySelectorAll(':scope > .embla__slide').forEach((slide, index, slides) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${index + 1} of ${slides.length}`);
    });
    const autoplayEnabled = this.config.autoplay && !this.reducedMotion.matches && document.body.dataset.reduceMotion !== 'true' && !window.Shopify?.designMode;
    const plugins = autoplayEnabled ? [this.autoplayModule.A({delay: this.config.delay * 1000, playOnInit: false, stopOnInteraction: false, stopOnMouseEnter: false, stopOnFocusIn: false})] : [];
    this.emblaApi = this.engine.E(this.nodes.viewport, {
      align: 'start', containScroll: 'trimSnaps', loop: this.config.loop,
      duration: this.reducedMotion.matches ? 0 : 25,
      watchDrag: (_api, event) => event.target.closest('aoratos-group-carousel') === this && !event.target.closest('input, textarea, select, button, [contenteditable="true"]')
    }, plugins);
    const setupControls = () => {
      this.dataset.overflow = String(this.emblaApi.scrollSnapList().length > 1);
      this.helpers.setupListingEmblaFeatures(this);
      this.syncAutoplay();
    };
    this.emblaApi.on('reInit', setupControls);
    this.emblaApi.on('pointerUp', () => this.syncAutoplay());
    if (previousItem) {
      const index = this.emblaApi.slideNodes().findIndex(slide => slide.contains(previousItem));
      if (index >= 0) this.emblaApi.scrollTo(index, true);
    }
    this.toggleAttribute('data-ready', true);
    this.pauseButton?.toggleAttribute('hidden', !autoplayEnabled);
    setupControls();
  }

  enableNavigation() { this.helpers.enableListingNavigation(this); }
  enableDots() { this.helpers.enableListingDots(this); }

  syncAutoplay() {
    const autoplay = this.emblaApi?.plugins().autoplay;
    if (!autoplay) return;
    const shouldPlay = this.dataset.overflow === 'true' && this.nearViewport && !document.hidden && !this.paused && !this.hovered && !this.contains(document.activeElement);
    if (shouldPlay && !autoplay.isPlaying()) autoplay.play();
    else if (!shouldPlay) autoplay.stop();
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.observer?.disconnect();
    this.helpers?.destroyListingEmbla(this);
    this.removeAttribute('data-ready');
  }
}

if (!customElements.get('aoratos-group-carousel')) customElements.define('aoratos-group-carousel', AoratosGroupCarousel);
